import keys from 'lodash/keys';
import values from 'lodash/values';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Prompt, useLocation } from 'react-router-dom';
import { Button, Container, Header, Icon } from 'semantic-ui-react';

import { useTracking } from '../../../Context';
import { bem } from '../../../core/design/bem';
import { eventTags } from '../../../core/trackers/events';
import { useMe } from '../../../profile/hooks';
import store from '../../../shared/Store';
import { useMultiWorkshopRegistration } from '../../../workshop-session/store/workshopSessions.hooks';
import { findPossibleRequiredSlots } from './utils';

const css = bem('WorkshopTable');
const translationPrefix = 'workshops.workshop';

export const SaveIcon = ({ loading }) => {
  const name = loading ? 'refresh' : 'save';
  return <Icon name={name} loading={loading} />;
};

SaveIcon.defaultProps = {
  loading: false,
};

SaveIcon.propTypes = {
  loading: PropTypes.bool,
};

/**
 * combinedSelectedSessions => selectedSessions + agenda sessions
 * @param {*} props
 * @returns
 */
function WorkshopMultipleValidation(props) {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const me = useMe();
  const {
    selectedSessions,
    combinedSelectedSessions,
    onSelect,
    requiredChoiceTimeSlots,
    config,
    collection,
  } = props;
  const { loading, registerManySessions } = useMultiWorkshopRegistration(values(selectedSessions), {
    config,
  });
  const { pathname: currentLocation } = useLocation();
  const handleValidate = async () => {
    const res = await registerManySessions();

    if (res?.success) {
      trackEvent(eventTags.AGENDA_COMPLETE, {
        collection,
        selected: keys(selectedSessions).length,
        entityIds: values(selectedSessions).map((s) => s.workshopId),
      });
      if (me.flash?.agenda) {
        // Remove flash message
        store.updateUser({ 'flash.agenda': null });
      }
    }
    if (onSelect && res?.success) onSelect();
  };

  const {
    hasRequiredSlots,
    remainingToPickCount,
    requiredSelectedCount,
    requiredTimeSlots,
  } = findPossibleRequiredSlots(requiredChoiceTimeSlots, combinedSelectedSessions);

  return (
    <div className={css('validation')}>
      <Container>
        <div>
          <Header as="h2" className="text">
            {t(`${translationPrefix}.table.selected-sessions-count`, {
              count: keys(selectedSessions).length,
            })}
          </Header>
          {!!requiredTimeSlots.length && (
            <div className="selected-required-count">
              {t(`${translationPrefix}.table.selected-required-count`, {
                count: requiredSelectedCount,
                remaining: remainingToPickCount,
                total: requiredTimeSlots.length,
              })}
            </div>
          )}
        </div>
        <Button
          className="validate"
          primary
          icon
          labelPosition="right"
          onClick={handleValidate}
          disabled={!hasRequiredSlots}
        >
          <SaveIcon loading={loading} />
          {t(`${translationPrefix}.table.validate-sessions`)}
        </Button>
        {keys(selectedSessions).length > 0 ? (
          <Prompt
            key="selectedSessions"
            when
            message={(location) => {
              if (location.pathname === currentLocation) return null;
              return t('common.unsaved-changes', { pathname: location.pathname });
            }}
          />
        ) : (
          <Prompt
            key="requiredSlots"
            when={!hasRequiredSlots}
            message={(location) => {
              if (location.pathname === currentLocation) return null;
              return t('common.required-slots', {
                requiredSlots: requiredTimeSlots.length - requiredSelectedCount,
                pathname: location.pathname,
              });
            }}
          />
        )}
      </Container>
    </div>
  );
}

WorkshopMultipleValidation.defaultProps = {
  config: {},
  onSelect: undefined,
  requiredChoiceTimeSlots: {},
};

WorkshopMultipleValidation.propTypes = {
  config: PropTypes.object,
  collection: PropTypes.string.isRequired,
  selectedSessions: PropTypes.object.isRequired,
  combinedSelectedSessions: PropTypes.object.isRequired,
  onSelect: PropTypes.func,
  requiredChoiceTimeSlots: PropTypes.object,
};

export default WorkshopMultipleValidation;
